/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { ShopperSearch } from '@/scapi';
import {
    Carousel,
    CarouselContent,
    CarouselItem,
    CarouselNext,
    CarouselPrevious,
    type CarouselSize,
} from '@/components/carousel';
import { ProductTile, ProductTileProvider } from '@/components/product-tile';
import DynamicImageProvider from '@/providers/dynamic-image';
import withSuspense from '@/components/with-suspense';
import ProductCarouselSkeleton from './skeleton';
import { cn } from '@/lib/utils';
import type { ComponentType } from '@/components/region';
import { Component } from '@/components/region/component';
import { usePageDesignerMode } from '@salesforce/storefront-next-runtime/design/react/core';
import { CarouselSection } from '@/components/carousel-section';
import { IMAGE_PRESETS } from '@/lib/image-presets';

const carouselItemImageWidths = [...IMAGE_PRESETS.RECOMMENDATION.widths];

export interface ProductCarouselProps {
    /** Array of product search hits to display in the carousel */
    products: ShopperSearch.schemas['ProductSearchHit'][];
    /** Optional title to display above the carousel */
    title?: string;
    /** Optional subtitle displayed below the title */
    subtitle?: string;
    /** Optional "Shop all" link URL displayed next to the title */
    shopAllUrl?: string;
    /** Optional label for the "Shop all" link. Defaults to "Shop all" */
    shopAllText?: string;
    /** Optional className for the title heading. Defaults to text-2xl md:text-3xl font-normal text-foreground tracking-tight */
    titleClassName?: string;
    /** Optional className to apply to the carousel wrapper */
    className?: string;
    /** Optional Page Designer component for container rendering mode */
    component?: ComponentType;
    /**
     * CART-18 — when `true`, the carousel signals tiles to suppress their
     * Quickview affordance. Defaults to `false` so existing PDP / Account
     * Overview / homepage carousels keep their current behaviour.
     *
     * Today the OOTB `<ProductTile>` does not render a Quickview affordance,
     * so this prop reserves the contract for the cart context per HLD #61
     * (Quickview is design-undefined for cart recs and should stay off). When
     * the Quickview affordance lands on `<ProductTile>`, plumb this flag
     * through `ProductTileProvider` rather than passing it per-tile.
     */
    disableQuickview?: boolean;
    /**
     * When `true`, every tile in the carousel hides its wishlist heart icon
     * (forwarded to `<ProductTile disableWishlist>`). Used by the
     * cart-recommendations surface where wishlist is suppressed by design.
     */
    disableWishlist?: boolean;
    /**
     * When `true`, every tile in the carousel hides its Quick Add hover
     * button (forwarded to `<ProductTile disableQuickAdd>`). Used by the
     * cart-recommendations surface where the shopper is sent to the PDP
     * instead of an inline add-to-cart.
     */
    disableQuickAdd?: boolean;
    /**
     * Title alignment forwarded to `<CarouselSection>`. Defaults to `'left'`
     * (back-compat with the existing PLP/homepage carousels). Use `'center'`
     * for the cart-recommendations layout.
     */
    titleAlign?: 'left' | 'center';
    /**
     * Position of the prev/next chevrons. `'edges'` (default) keeps the
     * legacy edge-positioned arrows. `'header'` moves them into the title
     * row (cart-recommendations layout).
     */
    arrowsPosition?: 'edges' | 'header';
}

/**
 * ProductCarousel component displays a horizontal carousel of product tiles.
 *
 * This component renders a responsive carousel with navigation controls that displays
 * a collection of products in a scrollable horizontal layout. It's commonly used
 * for featured products, recommendations, or product collections on home and category pages.
 *
 * @param props - The component props
 * @param props.products - Array of product search hits to display in the carousel
 * @param props.title - Optional title to display above the carousel
 * @param props.className - Optional className to apply to the carousel wrapper
 *
 * @returns JSX element representing the product carousel, or a translated "No products found" message
 *
 * @example
 * ```tsx
 * // Basic usage with products array
 * <ProductCarousel
 *   products={searchResult.hits}
 *   title="Featured Products"
 * />
 *
 * // Usage without title
 * <ProductCarousel products={products} />
 *
 * // Usage with custom className
 * <ProductCarousel products={products} className="mt-8" />
 * ```
 *
 * @since 1.0.0
 */
export default function ProductCarousel({
    products,
    title,
    subtitle,
    shopAllUrl,
    shopAllText,
    titleClassName,
    className,
    component,
    // CART-18 — reserved for the cart context Quickview opt-out (see prop docs).
    // Currently a no-op because `<ProductTile>` has no Quickview affordance,
    // but kept on the API so callers (e.g. cart recs) can already declare the
    // intent. When Quickview lands, plumb through `ProductTileProvider`.
    disableQuickview: _disableQuickview,
    disableWishlist,
    disableQuickAdd,
    titleAlign,
    arrowsPosition,
}: ProductCarouselProps): ReactNode {
    const { t } = useTranslation('product');
    const { isDesignMode } = usePageDesignerMode();
    const productsRegion = component?.regions?.find((region) => region.id === 'products');
    const regionComponents = productsRegion?.components ?? [];
    const resolvedTitle = title || ''; // put empty string as the title since dont currently have i18n for these default values.

    // When there are no products and no region components, only show the prompt in
    // Page Designer design mode (to guide the content author). On the live storefront
    // render nothing to avoid showing a confusing placeholder to shoppers.
    if ((!products || products.length === 0) && regionComponents.length === 0) {
        if (!isDesignMode) {
            return null;
        }
        return (
            <div className={cn('section-container py-6', className)}>
                <div role="status" aria-live="polite">
                    {t('selectProduct')}
                </div>
            </div>
        );
    }

    const isCartRecommendationsVariant = titleAlign === 'center' && arrowsPosition === 'header';

    const renderItems = (itemClassName: string) =>
        regionComponents.length > 0 && productsRegion ? (
            regionComponents.map((comp) => {
                const typedComp = comp as ComponentType;
                const key = typedComp.contentLinkUuid ?? typedComp.id;
                return (
                    <CarouselItem key={key} className={itemClassName}>
                        <div className="w-full max-w-full min-w-0 flex">
                            <Component component={typedComp} regionId={productsRegion.id} className="h-full w-full" />
                        </div>
                    </CarouselItem>
                );
            })
        ) : (
            <ProductTileProvider>
                <DynamicImageProvider value={{ widths: carouselItemImageWidths }}>
                    {products.map((product) => (
                        <CarouselItem key={product.productId} className={itemClassName}>
                            <div className="w-full max-w-full min-w-0 flex">
                                <ProductTile
                                    product={product}
                                    disableWishlist={disableWishlist}
                                    disableQuickAdd={disableQuickAdd}
                                    className="h-full w-full"
                                />
                            </div>
                        </CarouselItem>
                    ))}
                </DynamicImageProvider>
            </ProductTileProvider>
        );

    const renderSection = ({
        itemClassName,
        sectionClassName,
    }: {
        itemClassName: string;
        sectionClassName?: string;
    }) => (
        <CarouselSection
            title={resolvedTitle}
            subtitle={subtitle}
            shopAllUrl={shopAllUrl}
            shopAllText={shopAllText}
            titleClassName={titleClassName}
            titleAlign={titleAlign}
            arrowsPosition={arrowsPosition}
            className={cn(className, sectionClassName)}
            ariaLabel={`${resolvedTitle} carousel`}>
            {renderItems(itemClassName)}
        </CarouselSection>
    );

    const renderAtomicCartSection = ({
        size,
        itemClassName,
        sectionClassName,
        showNavigation,
    }: {
        size: CarouselSize;
        itemClassName: string;
        sectionClassName: string;
        showNavigation: boolean;
    }) => (
        <div className={cn('w-full min-w-0 max-w-full overflow-x-hidden pt-[60px]', sectionClassName, className)}>
            <Carousel className="w-full" size={size} opts={{ align: 'start' }} aria-label={`${resolvedTitle} carousel`}>
                {resolvedTitle ? (
                    <div className={cn('relative flex items-center', 'top-[-2.5rem] justify-between mb-4')}>
                        <h2 className={titleClassName ?? 'font-serif text-xl leading-7 text-card-foreground text-left'}>
                            {resolvedTitle}
                        </h2>
                        {showNavigation && size === 'Small' ? (
                            <div className="flex items-center gap-2 shrink-0">
                                <CarouselPrevious className="static left-auto right-auto translate-y-0" />
                                <CarouselNext className="static left-auto right-auto translate-y-0" />
                            </div>
                        ) : null}
                    </div>
                ) : null}
                <div>
                    <CarouselContent>{renderItems(itemClassName)}</CarouselContent>
                    {showNavigation && size !== 'Small' ? (
                        <>
                            <CarouselPrevious className={cn(size !== 'middle' && 'left-2 translate-x-0 z-10')} />
                            <CarouselNext className={cn(size !== 'middle' && 'right-2 translate-x-0 z-10')} />
                        </>
                    ) : null}
                </div>
            </Carousel>
        </div>
    );

    if (isCartRecommendationsVariant) {
        return (
            <>
                {/* Figma cart recs mobile variant */}
                {renderAtomicCartSection({
                    size: 'Small',
                    itemClassName: 'basis-44 py-1 flex',
                    sectionClassName: 'md:hidden',
                    showNavigation: false,
                })}
                {/* Figma cart recs desktop variant */}
                {renderAtomicCartSection({
                    size: 'middle',
                    itemClassName: 'basis-[19.5rem] py-1 flex',
                    sectionClassName: 'hidden md:block',
                    showNavigation: true,
                })}
            </>
        );
    }

    return renderSection({
        itemClassName: 'w-[21.75rem] md:w-64 2xl:w-72 basis-auto py-1 flex',
    });
}

/**
 * ProductCarouselWithSuspense component provides a ProductCarousel wrapped with a Suspense boundary.
 *
 * This component automatically shows the ProductCarouselSkeleton as a fallback while the
 * ProductCarousel is loading, providing better user experience during data fetching.
 *
 * When used with a `resolve` prop, the resolved data should be a ProductSearchResult
 * that will be passed as the `products` prop to the ProductCarousel component.
 *
 * @example
 * ```tsx
 * // Basic usage with Suspense boundary
 * <ProductCarouselWithSuspense
 *   products={searchResult.hits}
 *   title="Featured Products"
 * />
 *
 * // Usage with promise resolution as a prop
 * <ProductCarouselWithSuspense
 *   resolve={searchResultPromise}
 *   title="Featured Products"
 * />
 *
 * // Usage in a page with streaming
 * function HomePage() {
 *   return (
 *     <div>
 *       <Hero />
 *       <ProductCarouselWithSuspense resolve={searchResultPromise} title="Shop Products" />
 *     </div>
 *   );
 * }
 * ```
 */
export const ProductCarouselWithSuspense = withSuspense(ProductCarouselWithData, {
    fallback: (props) => <ProductCarouselSkeleton {...props} />,
});

/**
 * Internal component that handles data transformation for ProductCarousel.
 * This component receives the resolved data and transforms it to the expected format.
 * Only supports ProductSearchResult and ProductSearchHit types for simplicity.
 */
export function ProductCarouselWithData({
    data,
    title,
    ...props
}: {
    data?: ShopperSearch.schemas['ProductSearchResult'] | ShopperSearch.schemas['ProductSearchHit'][];
    title?: string;
    [key: string]: unknown;
}) {
    // If data is provided (from resolve), extract products from it
    if (data) {
        // Handle ProductSearchResult (has hits property)
        if ('hits' in data && Array.isArray(data.hits)) {
            return <ProductCarousel products={data.hits} title={title} {...props} />;
        }

        // Handle direct ProductSearchHit array
        if (Array.isArray(data)) {
            return <ProductCarousel products={data} title={title} {...props} />;
        }
    }

    // If no data or unsupported format, render empty state
    return <ProductCarousel products={[]} title={title} {...props} />;
}
